/**
 * "Remind me about this" (Slack, core logic/later): the presets — In 20 minutes, In 1 hour, In 3 hours, Tomorrow,
 * Next week — each with the time it lands on, and "Custom…" (a day and a time, in 30-minute steps). Also used to
 * snooze or change a Later item's reminder.
 */
import { Later, type RemindPreset } from "@bundle/core";
import { useMemo, useState } from "react";
import { Button, DatePicker, Pressable, Select, Sheet, Stack, Text } from "../primitives/index.ts";
import { useFormat, useLocale, useT } from "../i18n/I18nProvider.tsx";

export type RemindSheetProps = {
  open: boolean;
  onClose: () => void;
  /** A preset or an exact time (custom) was chosen. */
  onPick: (when: RemindPreset | number) => void;
  /** Now and my UTC offset in minutes (the presets' times). */
  now: number;
  offsetMinutes: number;
  title?: string;
  testID: string;
};

const pad = (n: number) => (n < 10 ? `0${n}` : String(n));

function ymd(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Every half hour of the day: "HH:MM". */
const SLOTS = Array.from({ length: 48 }, (_, i) => `${pad(Math.floor(i / 2))}:${i % 2 === 0 ? "00" : "30"}`);

/** A day (calendar) and a time (30-minute steps), local; `onSet` gets the timestamp. Slack's "Custom time". */
export function CustomTimePicker({ now, offsetMinutes, setLabel, onSet, onCancel, testID }: { now: number; offsetMinutes: number; setLabel: string; onSet: (at: number) => void; onCancel: () => void; testID: string }) {
  const t = useT();
  const fmt = useFormat();
  const locale = useLocale();
  const tomorrow = useMemo(() => new Date(Later.presetTime("tomorrow", now, offsetMinutes)), [now, offsetMinutes]);
  const [day, setDay] = useState(() => ymd(tomorrow));
  const [slot, setSlot] = useState("09:00");
  const at = (() => {
    const [y, m, d] = day.split("-").map(Number);
    const [h, min] = slot.split(":").map(Number);
    return new Date(y!, m! - 1, d!, h!, min!).getTime();
  })();
  return (
    <Stack gap="3" padding="4">
      <Text weight="semibold">{t("remind.date")}</Text>
      <DatePicker value={day} onChange={(d) => setDay(d)} locale={locale === "pt-BR" ? "pt-BR" : "en"} testID={`${testID}-date`} />
      <Select<string>
        label={t("remind.time")}
        value={slot}
        onChange={setSlot}
        options={SLOTS.map((s) => {
          const [h, m] = s.split(":").map(Number);
          return { value: s, label: fmt.time(new Date(2026, 0, 1, h!, m!).getTime()) };
        })}
        testID={`${testID}-time`}
      />
      <Stack direction="horizontal" gap="2" justify="end">
        <Button variant="ghost" label={t("common.cancel")} onPress={onCancel} testID={`${testID}-custom-cancel`} />
        <Button variant="primary" label={setLabel} disabled={at <= now} onPress={() => onSet(at)} testID={`${testID}-custom-set`} />
      </Stack>
    </Stack>
  );
}

/** "Schedule message" → "Custom time" (and Reschedule): a sheet with the day and time. */
export function ScheduleSheet({ open, onClose, onPick, now, offsetMinutes, title, setLabel, testID }: { open: boolean; onClose: () => void; onPick: (at: number) => void; now: number; offsetMinutes: number; title: string; setLabel: string; testID: string }) {
  return (
    <Sheet open={open} onClose={onClose} title={title} testID={testID}>
      {open ? (
        <CustomTimePicker
          now={now}
          offsetMinutes={offsetMinutes}
          setLabel={setLabel}
          onCancel={onClose}
          onSet={(at) => {
            onPick(at);
            onClose();
          }}
          testID={testID}
        />
      ) : null}
    </Sheet>
  );
}

export function RemindSheet({ open, onClose, onPick, now, offsetMinutes, title, testID }: RemindSheetProps) {
  const t = useT();
  const fmt = useFormat();
  const [custom, setCustom] = useState(false);
  const close = () => {
    setCustom(false);
    onClose();
  };
  const pick = (when: RemindPreset | number) => {
    onPick(when);
    close();
  };
  return (
    <Sheet open={open} onClose={close} title={title ?? t("remind.title")} testID={testID}>
      {custom ? (
        <CustomTimePicker now={now} offsetMinutes={offsetMinutes} setLabel={t("remind.set")} onCancel={() => setCustom(false)} onSet={pick} testID={testID} />
      ) : (
        <Stack gap="0.5" padding="2">
          {Later.REMIND_PRESETS.map((p) => (
            <Pressable key={p} onPress={() => pick(p)} radius="md" accessibilityLabel={t(`remind.preset.${p}`)} testID={`${testID}-${p}`}>
              <Stack direction="horizontal" align="center" justify="between" gap="3" paddingX="3" paddingY="2" grow>
                <Text>{t(`remind.preset.${p}`)}</Text>
                <Text variant="footnote" color="text.tertiary">
                  {p === "tomorrow" || p === "nextWeek" ? fmt.timestamp(Later.presetTime(p, now, offsetMinutes)) : fmt.time(Later.presetTime(p, now, offsetMinutes))}
                </Text>
              </Stack>
            </Pressable>
          ))}
          <Pressable onPress={() => setCustom(true)} radius="md" accessibilityLabel={t("remind.custom")} testID={`${testID}-custom`}>
            <Stack paddingX="3" paddingY="2" grow>
              <Text>{t("remind.custom")}</Text>
            </Stack>
          </Pressable>
        </Stack>
      )}
    </Sheet>
  );
}
